import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, Truck, Users, Check, ArrowRight } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import dashboardScreenshot from "@/assets/dashboard-screenshot.png";
import workspaceScreenshot from "@/assets/workspace-screenshot.png";
import bidsScreenshot from "@/assets/bids-screenshot.png";

const benefits = [
  {
    title: "Everything in one place",
    description: "Every bid, every conversation, every document — organized by shipment from quote to delivery.",
    link: "See how it works",
    href: "/how-it-works",
    image: dashboardScreenshot,
  },
  {
    title: "Work with partners you already trust",
    description: "Add your existing brokers and carriers as partners. They get a free invite to join and bid on your loads.",
    link: "Learn about partners",
    href: "/how-it-works",
    image: workspaceScreenshot,
  },
  {
    title: "Compare bids side by side",
    description: "See all bids in one view — rate, transit time, and carrier details. Accept the best one with a single click.",
    link: "Get started free",
    href: "/register",
    image: bidsScreenshot,
  },
];

export default function MembershipCards() {
  const { isAuthenticated } = useAuth();

  const handleJoinClick = (role: string) => {
    if (!isAuthenticated) {
      window.location.href = `/register?role=${role}`;
    } else {
      window.location.href = `/select-membership/${role}`;
    }
  };

  return (
    <div>
      {/* Benefit cards */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="flex flex-col">
                <div className="rounded-xl mb-4 p-3 overflow-hidden" style={{ backgroundColor: "var(--muted)", height: "220px" }}>
                  <img
                    src={benefit.image}
                    alt={benefit.title}
                    className="w-full h-full object-cover object-top rounded-lg shadow-sm"
                  />
                </div>
                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground text-sm mb-3 flex-1">{benefit.description}</p>
                <a
                  href={benefit.href}
                  className="text-primary text-sm font-medium flex items-center gap-1 hover:underline"
                >
                  {benefit.link}
                  <ArrowRight className="w-3 h-3" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Role cards */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Who uses Fill The Trucks
            </h2>
            <p className="text-lg text-muted-foreground">
              Built for business owners who ship freight — with the brokers and carriers who move it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="flex flex-col hover-elevate transition-all duration-200" data-testid="card-membership-shipper">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="w-5 h-5 text-primary" />
                  <CardTitle className="text-xl">Shipper</CardTitle>
                </div>
                <CardDescription>For businesses shipping freight</CardDescription>
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">Post your shipment once and get competitive bids from your partners, all in one place.</p>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col flex-1 space-y-6">
                <ul className="space-y-2 flex-1">
                  {[
                    "Post once — your partners are notified automatically",
                    "Receive and compare bids in one place",
                    "All communications in one Shipment Workspace",
                    "BOL management and delivery confirmation",
                    "Carrier and broker ratings",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground text-center">Free · 3 shipments/month · $25/mo for unlimited</p>
                  <Button className="w-full" size="lg" data-testid="button-join-shipper"
                    onClick={() => handleJoinClick("shipper")}>
                    Start Free
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="flex flex-col hover-elevate transition-all duration-200" data-testid="card-membership-carrier">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Truck className="w-5 h-5 text-primary" />
                  <CardTitle className="text-xl">Carrier</CardTitle>
                </div>
                <CardDescription>For trucking companies & owner-operators</CardDescription>
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">Get invited to bid on loads from shippers you work with. Upgrade to access the full public board.</p>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col flex-1 space-y-6">
                <ul className="space-y-2 flex-1">
                  {[
                    "Bid on loads your shipper partners invite you to",
                    "All communications in one Shipment Workspace",
                    "Build your reputation through completed shipments",
                    "Upgrade to search the full public load board",
                    "DOT/MC profile page",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground text-center">Free · bid on invited loads · $25/mo for full board access</p>
                  <Button className="w-full" size="lg" data-testid="button-join-carrier"
                    onClick={() => handleJoinClick("carrier")}>
                    Start Free
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="flex flex-col hover-elevate transition-all duration-200" data-testid="card-membership-broker">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-5 h-5 text-primary" />
                  <CardTitle className="text-xl">Broker</CardTitle>
                </div>
                <CardDescription>For freight brokers & intermediaries</CardDescription>
                <div className="mt-4">
                  <p className="text-sm text-muted-foreground">Get notified when your shipper clients post loads. Upgrade to access the full public board and find new shipper clients.</p>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col flex-1 space-y-6">
                <ul className="space-y-2 flex-1">
                  {[
                    "Bid on loads your shipper partners invite you to",
                    "Build reputation on both sides of freight",
                    "Upgrade to search and bid on the public board",
                    "Full Access: post loads on behalf of shipper clients",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground text-center">Free · bid on invited loads · $25–$45/mo for more</p>
                  <Button className="w-full" size="lg" data-testid="button-join-broker"
                    onClick={() => handleJoinClick("broker")}>
                    Start Free
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}